/**
 * Authentication utilities
 */
import { API_URL } from '@/config'

const TOKEN_KEY = 'auth_token'
export const AUTH_TOKEN_KEY = TOKEN_KEY
export const REFRESH_TOKEN_KEY = 'refresh_token'
const USER_EMAIL_KEY = 'user_email'

export type UserRole = 'member' | 'dev' | 'admin'

const ROLE_RANK: Record<UserRole, number> = {
  member: 0,
  dev: 1,
  admin: 2,
}

/**
 * Returns true if `actual` meets or exceeds `minimum` in the role hierarchy:
 * `member < dev < admin`. An undefined actual role is treated as `member`
 * (least privilege).
 */
export function meetsMinimum(actual: UserRole | undefined, minimum: UserRole): boolean {
  return ROLE_RANK[actual ?? 'member'] >= ROLE_RANK[minimum]
}

export interface AuthUser {
  email: string
  role: UserRole
}

/**
 * Store auth token in localStorage
 */
export function setAuthToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * Store refresh token in localStorage
 */
export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token)
}

/**
 * Get refresh token from localStorage
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

/**
 * Get auth token from localStorage
 */
export function getAuthToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * Remove auth token and refresh token from localStorage
 */
export function clearAuthToken(): void {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem(USER_EMAIL_KEY)
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return !!getAuthToken()
}

/**
 * Get the stored user email (set after successful token verification).
 */
export function getUserEmail(): string | null {
  return localStorage.getItem(USER_EMAIL_KEY)
}

/**
 * Get authorization header for API requests
 */
export function getAuthHeader(): Record<string, string> {
  const token = getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

/**
 * Get current user info
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  const token = getAuthToken()
  if (!token) return null

  try {
    const response = await fetch(`${API_URL}/api/v1/auth/me`, {
      headers: getAuthHeader()
    })

    if (!response.ok) {
      clearAuthToken()
      return null
    }

    const data = await response.json()
    if (data?.email) {
      localStorage.setItem(USER_EMAIL_KEY, data.email)
    }
    return data
  } catch (error) {
    console.error('Failed to get current user:', error)
    clearAuthToken()
    return null
  }
}

/**
 * Initiate OAuth login
 */
export function initiateLogin(): void {
  window.location.href = `${API_URL}/api/v1/auth/login`
}

/**
 * Single in-flight refresh. Concurrent 401 handlers await this same promise
 * instead of each POSTing /auth/refresh against a single-use rotating token
 * (which races, trips server-side replay detection, and logs out every device).
 * Mirrors the iOS AuthService.refreshTask pattern.
 */
let refreshInFlight: Promise<boolean> | null = null

/**
 * Attempt to refresh the access token using the stored refresh token.
 * Returns true if successful (new tokens stored), false if refresh failed.
 * Only a definitive rejection (401/403) logs the user out; transient failures
 * (5xx, 429, network error) keep tokens intact so the caller can retry.
 * Mirrors iOS AuthService (GH #109). Concurrent callers share one request.
 */
export async function refreshAccessToken(): Promise<boolean> {
  if (refreshInFlight) {
    return refreshInFlight
  }

  refreshInFlight = performTokenRefresh().finally(() => {
    refreshInFlight = null
  })

  return refreshInFlight
}

async function performTokenRefresh(): Promise<boolean> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) return false

  try {
    const response = await fetch(`${API_URL}/api/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    })

    if (response.status === 401 || response.status === 403) {
      logout()
      return false
    }
    if (!response.ok) return false

    const data = await response.json()
    setAuthToken(data.access_token)
    setRefreshToken(data.refresh_token)
    return true
  } catch {
    return false
  }
}

/**
 * Delete the current account (soft delete — deactivates and revokes every
 * session server-side). Returns whether the request succeeded; caller is
 * responsible for clearing local auth state and navigating away.
 */
export async function deleteAccount(): Promise<boolean> {
  try {
    const response = await fetch(`${API_URL}/api/v1/auth/me`, {
      method: 'DELETE',
      headers: getAuthHeader(),
    })
    return response.ok
  } catch (error) {
    console.error('Failed to delete account:', error)
    return false
  }
}

/**
 * Logout user
 */
export function logout(): void {
  // Clear the per-user event stream sequence number before removing the email key.
  const userEmail = localStorage.getItem(USER_EMAIL_KEY)
  if (userEmail) {
    localStorage.removeItem(`event_stream_seq_${userEmail}`)
  }
  clearAuthToken()
  window.location.href = '/'
}
